// useFormState — the draft / dirty / validate / save state machine for
// FormView (WS 4.1 substrate). Ports the legacy WorkspaceActionController
// semantics into a route-scoped hook shape.
//
// Lifetime: keyed by `(modelKey, recordRef)`. The host (RecordProvider)
// remounts via `key=...` on URL change so stale refs can't bleed across
// records. No singleton state; everything lives in component-local
// `useState`/`useRef`.
//
// Save dispatch is INJECTED by the consumer via `onCreate` / `onUpdate`
// callbacks — keeps the hook free of HTTP / runtime coupling and makes
// the contract trivially unit-testable.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FieldDef, FormElement, Json, RecordData } from "../../core";
import { collectInvisibleFieldNames, collectRequiredFieldNames } from "../../managers/formSheetHelpers";
import { buildSaveValues, collectRequiredMissing, isDirty as computeDirty } from "./buildSaveValues";

export type SaveResult =
    | { ok: true; record: RecordData }
    // `missing` is present (and non-empty) only on a required-field
    // validation failure — it carries the human labels for the summary
    // toast. Server / unexpected failures leave it undefined.
    | { ok: false; error: string; missing?: string[] };

export interface UseFormStateOptions {
    /** Server-side record loaded into the form. `null` ⇒ new-record draft. */
    initialRecord: RecordData | null | undefined;
    /** Whether this is a fresh draft (no existing recordRef). */
    isNew: boolean;
    /** Field-defs for the model (drives dirty-check semantics + validation). */
    fieldDefs: FieldDef[];
    /** Field names declared in the form view — the save payload is filtered
        to this set so list-view-only columns don't get clobbered. `null` ⇒
        no filter (every field-def is in play). */
    viewFieldNames?: string[] | null;
    /** Async save handler — invoked on create with the composed payload.
        Receives the post-extraction `values` (bare UUIDs for M2O, tuple
        arrays for M2M / O2M — wire-shape ready for backend save) AND the
        original `draft` (with `{id, display_name}` for M2O — preserves
        display labels for optimistic local rendering). Most consumers
        ignore `draft` and just persist `values`; only buffered relation
        editors (RelationListView) use it for optimistic table rows. */
    onCreate: (values: Record<string, Json>, draft?: RecordData) => Promise<RecordData>;
    /** Async save handler — invoked on update with the dirty-filtered payload.
        Same dual-shape semantics as `onCreate`. */
    onUpdate: (recordRef: string, values: Record<string, Json>, draft?: RecordData) => Promise<RecordData>;
    /** Current record's uuid (undefined on new-record draft). */
    recordRef?: string;
    /** Optional initial value for `isEditing`. When unset, defaults to
        `isNew` — new-record drafts start in edit mode, existing records
        start in read mode. Callers that always-edit (e.g. RelationListView's
        popup form opened from an editing parent) pass `true` so the
        first render already shows editor widgets, with no post-mount
        toggle flicker. */
    initialIsEditing?: boolean;
    /** Form view's sheet elements — used to compute which required fields
        are currently hidden (via `invisible="..."` predicate on the field
        or any enclosing section / notebook page) so required-validation
        can skip them. When unset, every required field is enforced
        (legacy behavior). */
    formElements?: FormElement[];
    /** Extra field names to merge into the required-validation invisible
        set. Lets callers (RelationListView's popup) auto-skip back-
        reference FKs without requiring the view author to declare
        `invisible="1"` on each one. */
    extraInvisibleFields?: string[];
}

export interface FormState {
    /** The current edited values. Read-mostly during view-mode; mutates on
        every field write during edit-mode. */
    draft: RecordData;
    /** Whether the form is in edit-mode. */
    isEditing: boolean;
    /** Whether this is a fresh new-record draft. */
    isNew: boolean;
    /** Whether the draft has unsaved changes vs the baseline. */
    isDirty: boolean;
    /** Save in flight. */
    isSaving: boolean;
    /** List of required-field labels missing a value — surface as a toast
        before dispatching save. Empty when valid. */
    requiredMissing: string[];
    /** Field NAMES flagged invalid by the most recent save attempt. Drives
        the inline red-border / red-label highlight in FormSheet. Empty
        until the user tries to save with required fields blank; a field
        clears from the set the moment the user writes a value to it. */
    invalidFields: ReadonlySet<string>;
    /** Enter edit-mode — snapshot the current draft as the dirty-check baseline. */
    onEdit: () => void;
    /** Exit edit-mode, reverting the draft to the baseline. Callers should
        guard with their own confirm UI when `isDirty`. */
    onDiscard: () => void;
    /** Write a single field on the draft. */
    onWriteField: (name: string, value: Json) => void;
    /** Replace the draft wholesale (e.g. after a quick-create or merge). */
    onSetDraft: (next: RecordData) => void;
    /** Validate then dispatch create / update via the injected callbacks. */
    onSave: () => Promise<SaveResult>;
}

// Collect the static per-field defaults surfaced by the kernel (`FieldDef.defaults`)
// into a draft seed. Mirrors the backend create path so a `/new` form opens
// pre-filled with the SAME values that would otherwise be written at save time.
function seedDefaults(fieldDefs: FieldDef[]): Record<string, Json> {
    const seed: Record<string, Json> = {};
    for (const fd of fieldDefs) {
        if (fd.defaults !== undefined && fd.defaults !== null) seed[fd.name] = fd.defaults;
    }
    return seed;
}

export function useFormState(opts: UseFormStateOptions): FormState {
    const { initialRecord, isNew, fieldDefs, viewFieldNames, onCreate, onUpdate, recordRef, initialIsEditing, formElements, extraInvisibleFields } = opts;

    // New-record drafts open pre-filled with the field defaults (default_get
    // semantics); existing records seed from the loaded server record.
    const initialDraft = (): RecordData =>
        isNew ? ({ id: "", ...seedDefaults(fieldDefs) } as RecordData) : (initialRecord ?? ({ id: "" } as RecordData));
    // Draft state — primary mutable surface. Seeds from initialRecord when
    // it arrives (server fetch races the mount); subsequent changes go through
    // onWriteField / onSetDraft / onDiscard.
    const [draft, setDraft] = useState<RecordData>(initialDraft);
    // Baseline for dirty-check. Snapshot at edit-mode entry, NOT at every
    // initialRecord refresh — the legacy code is explicit about this so a
    // background refetch during edit doesn't reset dirty-tracking.
    const [originalRecord, setOriginalRecord] = useState<RecordData>(initialDraft);
    const [isEditing, setIsEditing] = useState(initialIsEditing ?? isNew);
    const [isSaving, setIsSaving] = useState(false);
    // Field names flagged invalid by the last save attempt. Set on a failed
    // save (required blank); a field clears the instant the user writes to it.
    const [invalidFields, setInvalidFields] = useState<ReadonlySet<string>>(() => new Set());

    // Sync the draft with the server-loaded record when:
    //   - not in edit mode (a fresh fetch should replace draft cleanly), or
    //   - this is a new-record draft (no existing record on the wire).
    // Inside edit mode we deliberately ignore initialRecord changes so a
    // background refetch doesn't blow away the user's typing.
    const editingRef = useRef(isEditing);
    useEffect(() => { editingRef.current = isEditing; }, [isEditing]);
    useEffect(() => {
        if (editingRef.current && !isNew) return;
        if (initialRecord) {
            setDraft(initialRecord);
            setOriginalRecord(initialRecord);
        }
    }, [initialRecord, isNew]);

    // Deep-link / cold-cache fallback: when a `/new` form mounts before the
    // action load (which carries fieldDefs) has resolved, the initializer above
    // seeded an empty draft. Seed the defaults once when fieldDefs first arrive.
    // Existing draft values win (`...seed, ...prev`) so any keystrokes the user
    // managed in the gap aren't clobbered; the ref makes it strictly one-shot.
    const seededRef = useRef(false);
    useEffect(() => {
        if (!isNew || seededRef.current || fieldDefs.length === 0) return;
        const seed = seedDefaults(fieldDefs);
        seededRef.current = true;
        if (Object.keys(seed).length === 0) return;
        setDraft((prev) => ({ ...seed, ...prev }));
        setOriginalRecord((prev) => ({ ...seed, ...prev }));
    }, [isNew, fieldDefs]);

    const isDirty = useMemo(() => {
        if (!isEditing) return false;
        return computeDirty(draft, originalRecord);
    }, [draft, originalRecord, isEditing]);

    const invisibleFieldNames = useMemo(() => {
        const fromForm = formElements ? collectInvisibleFieldNames(formElements, draft) : undefined;
        if (!extraInvisibleFields || extraInvisibleFields.length === 0) return fromForm;
        const merged = new Set<string>(fromForm ?? []);
        for (const f of extraInvisibleFields) merged.add(f);
        return merged;
    }, [formElements, draft, extraInvisibleFields]);
    // Field names the form-view layout marks `required="1"` — so a layout
    // required field blocks save (matching the FormSheet `*`), even when its
    // model column is nullable.
    const viewRequiredFieldNames = useMemo(
        () => (formElements ? collectRequiredFieldNames(formElements) : undefined),
        [formElements],
    );
    const requiredMissingFields = useMemo(
        () => collectRequiredMissing(draft, fieldDefs, invisibleFieldNames, viewRequiredFieldNames),
        [draft, fieldDefs, invisibleFieldNames, viewRequiredFieldNames],
    );
    const requiredMissing = useMemo(() => requiredMissingFields.map((m) => m.label), [requiredMissingFields]);

    const onEdit = useCallback(() => {
        // Snapshot the current draft (not initialRecord) as the baseline —
        // draft is authoritative because fetchRecord may have merged form-
        // view-only fields (M2M, etc.) the list refetch would otherwise miss.
        setOriginalRecord(draft);
        setIsEditing(true);
    }, [draft]);

    const onDiscard = useCallback(() => {
        setDraft(originalRecord);
        setIsEditing(false);
    }, [originalRecord]);

    const onWriteField = useCallback((name: string, value: Json) => {
        setDraft((prev) => ({ ...prev, [name]: value }));
        // Clear the field's invalid flag as soon as the user touches it —
        // the red highlight should disappear while they're fixing it, not
        // linger until the next save attempt.
        setInvalidFields((prev) => {
            if (!prev.has(name)) return prev;
            const next = new Set(prev);
            next.delete(name);
            return next;
        });
    }, []);

    const onSetDraft = useCallback((next: RecordData) => {
        setDraft(next);
    }, []);

    const onSave = useCallback(async (): Promise<SaveResult> => {
        // No-op short-circuit on UPDATE with no changes — exit edit mode
        // without a wire call (the legacy guard).
        if (!isNew && !isDirty) {
            setIsEditing(false);
            return { ok: true, record: draft };
        }
        if (requiredMissingFields.length > 0) {
            // Flag every blank required field so FormSheet paints its border
            // + label red, and hand the labels back so the caller can raise
            // a single summary toast listing them all.
            setInvalidFields(new Set(requiredMissingFields.map((m) => m.name)));
            return {
                ok: false,
                error: `Required fields missing: ${requiredMissing.join(", ")}`,
                missing: requiredMissing,
            };
        }
        setInvalidFields(new Set());
        setIsSaving(true);
        try {
            const values = isNew
                ? buildSaveValues(draft, fieldDefs, viewFieldNames ?? null)
                : buildSaveValues(draft, fieldDefs, viewFieldNames ?? null, originalRecord);
            const saved = isNew
                ? await onCreate(values, draft)
                : await onUpdate(recordRef ?? "", values, draft);
            // Refresh draft + baseline with the server-returned record so
            // subsequent dirty checks start from the new ground truth.
            setDraft(saved);
            setOriginalRecord(saved);
            setIsEditing(false);
            return { ok: true, record: saved };
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            return { ok: false, error: message };
        } finally {
            setIsSaving(false);
        }
    }, [draft, fieldDefs, isDirty, isNew, onCreate, onUpdate, originalRecord, recordRef, requiredMissing, requiredMissingFields, viewFieldNames]);

    return {
        draft,
        isEditing,
        isNew,
        isDirty,
        isSaving,
        requiredMissing,
        invalidFields,
        onEdit,
        onDiscard,
        onWriteField,
        onSetDraft,
        onSave,
    };
}
